/* eslint-disable @scandipwa/scandipwa-guidelines/jsx-no-props-destruction */
/* eslint-disable max-lines */
/* eslint-disable react/forbid-prop-types */
/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/base-theme
 * @link https://github.com/scandipwa/base-theme
 */

import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import AttributesQuery from 'Query/Attributes.query';
import { LOADING_TIME } from 'Route/CategoryPage/CategoryPage.config';
import { RECOMMENDER_PDP_ID } from 'Route/ProductPage/ProductPage.config';
import {
    mapDispatchToProps as sourceMapDispatchToProps,
    mapStateToProps as sourceMapStateToProps,
    ProductPageContainer as SourceProductPageContainer
} from 'SourceRoute/ProductPage/ProductPage.container';
import ProductReducer from 'Store/Product/Product.reducer';
import { updateUrlRewrite } from 'Store/UrlRewrites/UrlRewrites.action';
import { TotalsType } from 'Type/MiniCart.type';
import isKsa from 'Util/Arabic/isKsa';
import { scrollToTop } from 'Util/Browser';
import BrowserDatabase from 'Util/BrowserDatabase';
import { withReducers } from 'Util/DynamicReducer';
import {
    getIsConfigurableParameterSelected,
    getVariantIndex as originalGetVariantIndex
} from 'Util/Product';
import { handleIosStickyHeader } from 'Util/PromoBanner/PromoBanner';
import { debounce, fetchQuery } from 'Util/Request';
import {
    convertQueryStringToKeyValuePairs,
    getQueryParam,
    objectToUri,
    removeQueryParamWithoutHistory,
    updateQueryParamWithoutHistory
} from 'Util/Url';

import ProductPageComponent from './ProductPage.component';

/** @namespace Scandipwa/Route/ProductPage/Container/getVariantIndex */
export const getVariantIndex = (variants, options) => {
    // Hack to use color, instead of search by attributes
    const { color } = options;

    if (color && Object.keys(options).length === 1) {
        const withStock = variants.findIndex(
            ({ color: vColor, salable_qty }) => (+color === +vColor && +salable_qty > 0)
        );

        return withStock !== -1 ? withStock : variants.findIndex(({ color: vColor }) => +color === +vColor);
    }

    return originalGetVariantIndex(variants, options);
};

export const BreadcrumbsDispatcher = import(
    /* webpackMode: "lazy", webpackChunkName: "dispatchers" */
    'Store/Breadcrumbs/Breadcrumbs.dispatcher'
);

export const MetaDispatcher = import(
    /* webpackMode: "lazy", webpackChunkName: "dispatchers" */
    'Store/Meta/Meta.dispatcher'
);

export const ProductDispatcher = import(
    /* webpackMode: "lazy", webpackChunkName: "dispatchers" */
    'Store/Product/Product.dispatcher'
);

/** @namespace Scandipwa/Route/ProductPage/Container/mapStateToProps */
export const mapStateToProps = (state) => ({
    ...sourceMapStateToProps(state),
    totals: state.CartReducer.cartTotals,
    isSignedIn: state.MyAccountReducer.isSignedIn,
    categoryId: state.CategoryReducer.category?.id,
    content_blocks: state.ProductReducer.content_html,
    customer: state.MyAccountReducer.customer,
    locale: state.ConfigReducer.locale,
    basePath: state.ConfigReducer.base_link_url,
    urlRewrite: state.UrlRewritesReducer.urlRewrite
});

/** @namespace Scandipwa/Route/ProductPage/Container/mapDispatchToProps */
export const mapDispatchToProps = (dispatch) => ({
    ...sourceMapDispatchToProps(dispatch),
    updateUrlRewrite: (urlRewrite, requestedUrl) => dispatch(updateUrlRewrite(urlRewrite, requestedUrl))
});

/** @namespace Scandipwa/Route/ProductPage/Container */
export class ProductPageContainer extends SourceProductPageContainer {
    linkCheckDone = false;

    containerFunctions = {
        ...this.containerFunctions,
        getLink: this.getLink.bind(this),
        updateUrl: this.updateUrl.bind(this),
        getSelectedCustomizableOptions: this.getSelectedCustomizableOptions.bind(this),
        createArrayOfIds: this.createArrayOfIds.bind(this)
    };

    static propTypes = {
        ...super.propTypes,
        totals: TotalsType.isRequired,
        isSignedIn: PropTypes.bool.isRequired,
        navigation: PropTypes.shape(PropTypes.shape).isRequired,
        categoryId: PropTypes.number,
        customer: PropTypes.object,
        locale: PropTypes.string.isRequired,
        basePath: PropTypes.string.isRequired,
        content_blocks: PropTypes.array
    };

    static defaultProps = {
        ...super.defaultProps,
        categoryId: 0,
        customer: {},
        content_blocks: []
    };

    timeoutId = null;

    static getDerivedStateFromProps(props, state) {
        const {
            product: {
                sku,
                variants,
                configurable_options,
                options,
                productOptionsData
            }
        } = props;

        const {
            currentProductSKU: prevSKU,
            productOptionsData: prevOptionData
        } = state;

        const currentProductSKU = prevSKU === sku ? '' : prevSKU;

        /**
         * If the product we expect to load is loaded -
         * reset expected SKU
         */
        if (!configurable_options && !variants) {
            return {
                currentProductSKU
            };
        }

        const { search } = window.location;
        // PARAMS UPDATE 1
        const parameters = Object.entries(convertQueryStringToKeyValuePairs(search))
            .reduce((acc, [key, value]) => {
                if (key === 'color' || key === 'size') {
                    return { ...acc, [key]: value };
                }

                return { ...acc };
            }, {});

        const newOptionsData = options.reduce((acc, { option_id, required }) => {
            if (required) {
                acc.push(option_id);
            }

            return acc;
        }, []);

        const prevRequiredOptions = productOptionsData?.requiredOptions || [];
        const requiredOptions = [...prevRequiredOptions, ...newOptionsData];

        return {
            parameters,
            currentProductSKU,
            productOptionsData: {
                ...prevOptionData, ...productOptionsData, requiredOptions
            }
        };
    }

    componentDidMount() {
        /**
         * Always request product information. In this case we will have updated data.
         * Service worker will return previous information and updated new information
         * through broadcast.
         */
        this.requestProduct();

        /**
         * Always make sure the navigation switches into the MENU tab
         * */
        this.updateNavigationState();

        /**
         * Ensure transition PDP => homepage => PDP always having proper meta
         */
        this.updateMeta();

        /**
         * Make sure to update header state, the data-source will
         * define the correct information to use for update
         * (it can be a product, history state product or an empty object).
         */
        this.updateHeaderState();
        this.updateBreadcrumbs();
        this.requestScarabPush();
        handleIosStickyHeader();

        /**
         * Scroll page top in order to display it from the start
         */
        scrollToTop();

        this.loadAttributesData();
    }

    componentDidUpdate(prevProps) {
        const {
            isOffline,
            productSKU,
            product,
            location
        } = this.props;

        const {
            productSKU: prevProductSKU,
            product: prevProduct,
            location: prevLocation
        } = prevProps;

        const { sku: stateSKU } = history?.state?.state?.product || {};
        const { isSignedIn } = this.props;
        const { isSignedIn: prevIsSignedIn = false } = prevProps;
        const dataSource = this.getDataSource();
        const { variants = [] } = dataSource;

        if (isSignedIn !== prevIsSignedIn) {
            this.requestProduct();
        }

        if (!this.linkCheckDone && variants.length && variants[0].color) {
            this.linkCheckDone = true;

            const { history, productSKU } = this.props;
            const { search, pathname } = window.location;
            const params = new URLSearchParams(search);

            if (!params.has('color')) {
                const requestedSku = getQueryParam('sku', window.location);

                // eslint-disable-next-line fp/no-let
                let sameStyleColor = false;

                // eslint-disable-next-line fp/no-let
                let color = variants.reduce((acc, variant) => {
                    const {
                        sku,
                        stock_status,
                        color,
                        style_number
                    } = variant;

                    if (!requestedSku && stock_status === 'IN_STOCK' && productSKU === style_number) {
                        sameStyleColor = color;
                    }

                    if (acc) {
                        return acc;
                    }

                    if (requestedSku && requestedSku === sku) {
                        return color;
                    }

                    if (!requestedSku && stock_status === 'IN_STOCK') {
                        return color;
                    }

                    return false;
                }, false);

                if (!sameStyleColor && !color) {
                    color = variants.reduce((acc, variant) => {
                        const {
                            sku,
                            stock_status,
                            color,
                            style_number
                        } = variant;

                        if (!requestedSku && productSKU === style_number) {
                            sameStyleColor = color;
                        }

                        if (acc) {
                            return acc;
                        }

                        if (requestedSku && requestedSku === sku) {
                            return color;
                        }

                        if (!requestedSku && stock_status === 'IN_STOCK') {
                            return color;
                        }

                        return false;
                    }, false);
                }

                if (sameStyleColor) {
                    params.set('color', sameStyleColor);
                    params.delete('sku');
                } else if (color) {
                    params.set('color', color);
                    params.delete('sku');
                }

                history.replace(
                    decodeURIComponent(`${ pathname }?${ params }`),
                    history.location.state
                );
            }

            // TODO: do we need removeSizeFromUrl() ?
        }

        this.redirectIfSizeIsNotAvailable();

        if (isOffline) {
            debounce(this.setOfflineNoticeSize, LOADING_TIME)();
        }

        /**
         * We should also update product based data if, the URL
         * rewrite SKU has changed to matching the product history SKU
         * one. At this point there could be sufficient data for
         * some updates (i.e. header state).
         */
        if (
            productSKU !== prevProductSKU
            && stateSKU === productSKU
        ) {
            this.updateHeaderState();
        }

        /**
         * If product object was changed => it is loaded => we need to
         * update product specific information, i.e. breadcrumbs.
         */
        if (JSON.stringify(product) !== JSON.stringify(prevProduct)) {
            this.updateBreadcrumbs();
            this.updateHeaderState();
            this.updateMeta();
        }

        this._addToRecentlyViewedProducts();

        if (location && prevLocation && location.search === prevLocation.search) {
            window.blockPDPRefresh = false;
        }
    }

    __construct(props) {
        super.__construct(props);

        const { search } = window.location;

        // PARAMS UPDATE 1
        const parameters = Object.entries(convertQueryStringToKeyValuePairs(search))
            .reduce((acc, [key, value]) => {
                if (key === 'color' || key === 'size') {
                    return { ...acc, [key]: value };
                }

                return { ...acc };
            }, {});

        // eslint-disable-next-line
        delete parameters.size;

        this.state = {
            parameters,
            currentProductSKU: '',
            activeProduct: null
        };
    }

    componentWillUnmount() {
        const { updateUrlRewrite } = this.props;

        if (updateUrlRewrite) {
            updateUrlRewrite({ type: 'PRODUCT' }, null);
        }

        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }
    }

    state = {
        ...this.state,
        productIds: '',
        isTriggered: false
    };

    loadAttributesData() {
        if (BrowserDatabase.getItem('productAttributes') === null) {
            fetchQuery(AttributesQuery.getProductAttributesQuery())
                .then(
                    /** @namespace Scandipwa/Route/ProductPage/Container/ProductPageContainer/loadAttributesData/then/catch/fetchQuery/then */
                    (result) => {
                        const expireTime = 3600;
                        BrowserDatabase.setItem(result, 'productAttributes', expireTime);
                    }
                )
                .catch(
                    /** @namespace Scandipwa/Route/ProductPage/Container/ProductPageContainer/loadAttributesData/then/catch */
                    (error) => {
                        // eslint-disable-next-line no-console
                        console.log(error);
                    }
                );
        }
    }

    requestScarabPush = () => {
        const { customer, isSignedIn, productSKU } = this.props;
        const { isTriggered } = this.state;

        if (window.ScarabQueue && !isTriggered && productSKU) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ isTriggered: true });
            if (isSignedIn) {
                if (customer) {
                    window.ScarabQueue.push(['setEmail', customer.email]); // Pushing customer email if Signed in
                    this.pushRecommenderData();
                }
            } else {
                this.pushRecommenderData();
            }
        }
        // eslint-disable-next-line no-magic-numbers
        this.timeoutId = setTimeout(this.requestScarabPush, 5000); // Every 5 s requesting recommended products
        setTimeout(() => {
            clearTimeout(this.timeoutId);
            // eslint-disable-next-line no-magic-numbers
        }, 15000);
    };

    pushRecommenderData() {
        const { productSKU, locale, totals: { items = [] } } = this.props;
        const { createArrayOfIds } = this.containerFunctions;

        window.ScarabQueue.push(['cart',
            items.map((product) => (
                {
                    item: product.sku,
                    price: product.prices.row_total_including_tax.value / product.quantity,
                    quantity: product.quantity
                }
            ))
        ]);
        window.ScarabQueue.push(['availabilityZone', locale]);
        window.ScarabQueue.push(['view', `${productSKU}`]);
        window.ScarabQueue.push(['recommend', {
            logic: isKsa() ? 'RELATED_LOGIC_PDP_KSA' : 'RELATED_LOGIC_PDP', // unique logic
            limit: 10,
            containerId: RECOMMENDER_PDP_ID, // unique container
            success(SC) {
                createArrayOfIds(SC.page.products.map((product) => product.id));
            }
        }]);

        window.ScarabQueue.push(['go']);
    }

    redirectIfSizeIsNotAvailable = () => {
        const { history } = this.props;
        const { search, pathname } = window.location;
        const params = new URLSearchParams(search);

        if (params.has('size') && params.has('color')) {
            const dataSource = this.getDataSource();
            const { variants = [] } = dataSource;

            const variantIndex = getVariantIndex(
                variants,
                { color: params.get('color'), size: params.get('size') }
            );

            if (variantIndex === -1) {
                params.delete('size');

                history.replace(
                    decodeURIComponent(`${ pathname }?${ params }`),
                    history.location.state
                );
            }
        }
    };

    getLink(key, value) {
        const { search, pathname } = window.location;

        const obj = {
            ...convertQueryStringToKeyValuePairs(search)
        };

        if (key) {
            obj[key] = value;
        }

        const query = objectToUri(obj);

        return `${pathname}${query}`;
    }

    getSelectedCustomizableOptions(values, updateArray = false) {
        const { productOptionsData } = this.state;

        if (updateArray) {
            this.setState({
                productOptionsData:
                    { ...productOptionsData, productOptionsMulti: values }
            });
        } else {
            this.setState({
                productOptionsData:
                    { ...productOptionsData, productOptions: values }
            });
        }
    }

    updateUrl(key, value, parameters, newProduct) {
        const { history } = this.props;

        const isParameterSelected = getIsConfigurableParameterSelected(parameters, key, value);

        if (isParameterSelected) {
            updateQueryParamWithoutHistory(key, value, history, window.location);
        } else {
            removeQueryParamWithoutHistory(key, history, window.location);
        }

        if (newProduct) {
            this.updateUrlStyleNumber(newProduct);
        }
    }

    updateUrlStyleNumber(newProduct) {
        const { history } = this.props;

        const { style_data: { style_url = '' } = {} } = newProduct;

        if (style_url) {
            window.blockPDPRefresh = true;
            history.replace(`${ style_url }${ window.location.search }`);
        }
    }

    updateBreadcrumbs() {
        const { updateBreadcrumbs, location, categoryId } = this.props;
        const { state: { prevCategoryId = null } = {} } = location;
        updateBreadcrumbs(this.getDataSource(), prevCategoryId || categoryId);
        if (categoryId && !prevCategoryId) {
            const historyState = history.state ? history.state.state : null;
            history.replaceState({
                ...history.state,
                state: {
                    ...historyState, prevCategoryId: categoryId
                }
            }, '');
        }
    }

    // eslint-disable-next-line react/sort-comp
    createArrayOfIds(ids) {
        this.setState({
            productIds: ids
        });
    }

    render() {
        return (
            <ProductPageComponent
              { ...this.props }
              { ...this.state }
              { ...this.containerFunctions }
              { ...this.containerProps() }
            />
        );
    }
}

export default withReducers({
    ProductReducer
})(withRouter(
    connect(mapStateToProps, mapDispatchToProps)(ProductPageContainer)
));
